import React, { useState, useCallback, useLayoutEffect, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Input, Div, Icon, Text } from '@pinpoint/design-kit';
import get from 'lodash.get';

import { ModelsDropdown } from 'src/components';
import BaseDialog from './base';

const defaultValues = {
  model: '',
  description: '',
};

function CreateRealtimeConfig({ isOpen, actions, models, profile }) {
  const [model, setModel] = useState(null);
  const { handleSubmit, errors, control, setValue, reset, register } = useForm({ defaultValues });
  const onSubmit = useCallback(
    (data) => {
      const modelId = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'id'
      );
      const modelName = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'name'
      );
      const dataModelType = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'dataModelType'
      );
      const dataModelSubType = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'dataModelSubType'
      );
      const newConfig = {
        score_type: 'model',
        config_metadata: {
          model_id: modelId,
          modelName,
          dataModelType,
          dataModelSubType,
          v: '39',
          profile,
        },
        description: data.description,
        default: false,
        enabled: false,
      };
      actions.handlers[0].on(newConfig);
      actions.handlers[0].after();
      actions.close.on();
    },
    [actions.close, actions.handlers, models, profile]
  );
  const formSubmit = handleSubmit(onSubmit);

  const resetForm = useCallback(() => {
    reset(defaultValues);
    setModel(null);
  }, [reset]);

  useEffect(() => {
    register({ name: 'model', type: 'custom' }, { required: 'Model is required' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelection = useCallback(
    (nextModel) => {
      setValue('model', nextModel);
      setModel(nextModel);
    },
    [setValue]
  );

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{
        ...actions,
        cancel: {
          ...actions.cancel,
          before: resetForm,
        },
        close: {
          ...actions.close,
          before: resetForm,
        },
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      items={{
        buttons: [
          {
            text: 'CREATE',
            textSize: 'body',
            close: false,
          },
        ],
        content: [
          <Div key="form-1" d="flex" flexDir="column" p={{ r: '.25rem' }}>
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Select a model
            </Text>
            <ModelsDropdown
              dropDownStyle={{
                w: '100%',
                h: '1.875rem',
                m: { y: '.375rem' },
                DropdownButtonOptions: {
                  h: '1.875rem',
                  w: '1.875rem',
                  rounded: { tr: 'md', br: 'md' },
                  border: { y: '1px solid', r: '1px solid', l: '0' },
                  borderColor: 'shade79',
                  align: 'center',
                  justify: 'center',
                  d: 'flex',
                  p: '0',
                },
                inputStyle: {
                  h: 'calc(1.875rem-1px)',
                  textColor: 'shade99',
                  flexGrow: '1',
                  p: { l: '0' },
                  rounded: 'none',
                },
                DivOptions: {
                  rounded: { l: 'md', r: '0' },
                  border: '1px solid',
                  borderColor: 'shade79',
                  p: { l: '0.75rem' },
                },
                openSuffix: <Icon color="shade66" name="UpArrow" size=".625rem" />,
                closeSuffix: <Icon color="shade66" name="DownArrow" size=".625rem" />,
              }}
              models={models}
              model={model}
              defaultText="Please select a model"
              onSelectModel={handleSelection}
            />
            <ErrorMessage
              errors={errors}
              name="model"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          <Div key="form-2" d="flex" flexDir="column" p={{ r: '.25rem' }}>
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Enter a description of the model (optional)
            </Text>
            <Controller
              borderColor="shade79"
              name="description"
              as={Input}
              control={control}
              m={{ t: '.25rem' }}
              h="1.875rem"
            />
          </Div>,
          <Div key="last-elem" />,
        ],
      }}
      title={{
        text: 'Select a Model',
        backgroundColor: 'marigold',
        color: 'white',
        textSize: 'title',
        icon: (
          <Icon
            name="AddBoxed"
            viewBox="0 0 14 14"
            size="2.375rem"
            color="white"
            alignSelf="center"
            justifySelf="center"
          />
        ),
      }}
    />
  );
}

CreateRealtimeConfig.propTypes = {
  profile: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  models: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};

export default CreateRealtimeConfig;
